import {
  Button,
  Checkbox,
  type GridColumns,
  GridList,
  Modal,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  spacing,
} from '@retailer-platform/shared-components'
import React, { type FunctionComponent, useCallback, useState } from 'react'
import { LoadingGenericBase } from '@instacart/ids-core'
import { useDashMessage } from 'intl/intl.hooks'
import {
  type NotificationsNotificationTypeAccount,
  useGetAccountByNotificationTypeQuery,
  useSubscribeNotificationMutation,
  useUnsubscribeNotificationMutation,
} from 'domains/notifications/api'
import { emailFilter, updateNotificationTypeListCache } from 'domains/notifications/utils'
import { Input } from 'gin-and-tonic/common/input/Input'
import { useNotifications } from 'legacy/common/utils/notifications/notifications.hooks'
import { useDomainAccessControl } from 'domains/notifications/utils/domain/accessControl'
import { Permission } from 'domains/notifications/access-control/permissions'
interface NotificationsModalProps {
  eventName: string
  retailerId: string
  notificationTypeId: string
  showModal: boolean
  setShowModal: (boolean) => void
}

export const NotificationsModal: FunctionComponent<NotificationsModalProps> = ({
  eventName,
  retailerId,
  notificationTypeId,
  showModal,
  setShowModal,
}) => {
  const hasAccess = useDomainAccessControl()

  // exclude internal users unless they have the NotificationsInternalUsers permission
  const excludeInternalUsers = !hasAccess({
    permissions: [Permission.NotificationsInternalUsers],
  })

  const { data, loading, error } = useGetAccountByNotificationTypeQuery({
    variables: {
      retailerId,
      notificationTypeId,
      excludeInternalUsers,
    },
  })

  const [subscribeNotificationMutation] = useSubscribeNotificationMutation()
  const [unsubscribeNotificationMutation] = useUnsubscribeNotificationMutation()
  const [searchEmail, setSearchEmail] = useState<string>('')

  const NotificationsFailedMessage = useDashMessage(
    'notificationsDomain.modal.message.update.failed'
  )
  const { notifyError } = useNotifications()

  const handleToastNotifications = useCallback(
    ({ errors }) => {
      if (errors) {
        notifyError(NotificationsFailedMessage, 'modern')
      }
    },
    [notifyError, NotificationsFailedMessage]
  )

  const handleSubscribe = useCallback(
    accountId => {
      subscribeNotificationMutation({
        variables: {
          accountId,
          notificationTypeId,
          retailerId,
        },
        optimisticResponse: {
          notificationSubscriptionCreate: {
            success: true,
            __typename: 'NotificationSubscriptionCreatePayload',
          },
        },
        update: (cache, { data, errors }) => {
          handleToastNotifications({ data, errors })
          updateNotificationTypeListCache({
            cache,
            data,
            accountId,
            retailerId,
            notificationTypeId,
            excludeInternalUsers,
            updatedValue: true,
          })
        },
      })
    },
    [
      retailerId,
      notificationTypeId,
      subscribeNotificationMutation,
      handleToastNotifications,
      excludeInternalUsers,
    ]
  )

  const handleUnsubscribe = useCallback(
    accountId => {
      unsubscribeNotificationMutation({
        variables: {
          accountId,
          notificationTypeId,
          retailerId,
        },
        optimisticResponse: {
          notificationSubscriptionDelete: {
            success: true,
            __typename: 'NotificationSubscriptionDeletePayload',
          },
        },
        update: (cache, { data, errors }) => {
          handleToastNotifications({ data, errors })
          updateNotificationTypeListCache({
            cache,
            data,
            accountId,
            retailerId,
            notificationTypeId,
            updatedValue: false,
            excludeInternalUsers,
          })
        },
      })
    },
    [
      retailerId,
      notificationTypeId,
      unsubscribeNotificationMutation,
      handleToastNotifications,
      excludeInternalUsers,
    ]
  )

  const columns: GridColumns<NotificationsNotificationTypeAccount> = [
    {
      id: 'name',
      width: 100,
      Header: useDashMessage('notificationsDomain.modal.columnTitle.name'),
      Cell: ({
        row: {
          original: { givenName, surname },
        },
      }) => <div>{`${givenName} ${surname}`}</div>,
    },
    {
      id: 'email',
      Header: useDashMessage('notificationsDomain.modal.columnTitle.email'),
      Cell: ({
        row: {
          original: { email },
        },
      }) => <div>{email}</div>,
    },
    {
      id: 'subscribe',
      width: 50,
      Header: useDashMessage('notificationsDomain.modal.columnTitle.subscribe'),
      Cell: ({
        row: {
          original: { isSubscribed, isEligible, accountId },
        },
      }) => {
        const message = useDashMessage('notificationsDomain.modal.cell.ineligible')

        return !isEligible ? (
          <div>{message}</div>
        ) : (
          <Checkbox
            id={accountId}
            checked={isSubscribed}
            onChange={() => {
              isSubscribed ? handleUnsubscribe(accountId) : handleSubscribe(accountId)
            }}
          />
        )
      },
    },
  ]

  // This is not placing on <Input placeholder={} /> because it will break the rule of hooks
  // Ref: https://reactjs.org/docs/hooks-rules.html
  const filterInputPlaceholder = useDashMessage('notificationsDomain.modal.search.placeholder')
  const closeButtonTitle = useDashMessage('notificationsDomain.modal.button.close')

  const handleModalClose = () => {
    setSearchEmail('')
    setShowModal(false)
  }

  return (
    <Modal onRequestClose={handleModalClose} isOpen={showModal} maxWidth={700}>
      {loading || error ? (
        <>
          <ModalHeader>{eventName}</ModalHeader>
          <ModalBody style={{ padding: spacing.X128 }}>
            <LoadingGenericBase />
          </ModalBody>
        </>
      ) : (
        <ModalContainer style={{ backgroundColor: 'white' }}>
          <ModalHeader>{eventName}</ModalHeader>
          <ModalBody>
            <Input
              value={searchEmail}
              autoFocus
              outerStyles={{ styles: 'display: flex;', name: 'notifications-email-filter-style' }}
              name="notifications-email-search"
              placeholder={filterInputPlaceholder}
              onChange={e => setSearchEmail(e.target.value)}
            />
            <GridList
              height={400}
              onLoadMore={() => {
                //
              }}
              data={emailFilter(data.accountsByNotificationType, searchEmail)}
              columns={columns}
              loadingMore={false}
              canLoadMore={false}
            />
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleModalClose}>{closeButtonTitle}</Button>
          </ModalFooter>
        </ModalContainer>
      )}
    </Modal>
  )
}
